import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { DateTime } from 'luxon';

import { Input } from 'antd';

import styled from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { tablet } from 'services/ui';
import { Colors } from 'context';
import { Button, DatePicker } from 'atoms';

import type { IHomeworkAssignment } from 'core';

export interface IHomeworkAssignmentFormValues {
  title: string;
  text: string;
  /** ТЗ 0026: «глава 7, задачи 1–4». */
  textbookRef: string;
  /** ISO date or null — informational in v1. */
  dueDate: string | null;
}

export interface IHomeworkAssignmentForm {
  /** Existing assignment to prefill when editing; null when creating. */
  assignment: IHomeworkAssignment | null;
  saveLoading: boolean;
  /** Returns true on success — the form is closed by the parent then. */
  onSave: (values: IHomeworkAssignmentFormValues) => Promise<boolean>;
  onCancel: () => void;
}

interface IFormValues {
  title: string;
  text: string;
  textbookRef: string;
  dueDate: Date | null;
}

/** Create/edit the lesson assignment (title + text + optional due date). */
export const HomeworkAssignmentForm = memo(
  ({ assignment, saveLoading, onSave, onCancel }: IHomeworkAssignmentForm) => {
    const { t } = useTranslation();

    /**
     * ТЗ 0026: текст задания перестал быть обязательным.
     *
     * Обязательность «сочини задание с нуля» и была причиной, по которой
     * заданий не заводили вовсе: на проде в завершённой группе на 41 занятие
     * их ноль. Теперь достаточно указать раздел учебника — но пустой формы
     * быть не должно, поэтому проверка форм-левельная: текст ИЛИ раздел.
     */
    const validationSchema = useMemo(
      () =>
        Yup.object().shape({
          title: Yup.string()
            .trim()
            .required(`${t('homework.assignment.form.titleRequired')}`),
          text: Yup.string().trim(),
          textbookRef: Yup.string().trim().max(255),
        }),
      [t],
    );

    const validateContent = (values: IFormValues) =>
      !values.text.trim() && !values.textbookRef.trim()
        ? { text: `${t('homework.assignment.form.contentRequired')}` }
        : {};

    const initialValues: IFormValues = useMemo(
      () => ({
        title: assignment?.title || '',
        text: assignment?.text || '',
        textbookRef: assignment?.textbookRef || '',
        dueDate: assignment?.dueDate ? DateTime.fromISO(assignment.dueDate).toJSDate() : null,
      }),
      [assignment],
    );

    return (
      <Formik<IFormValues>
        initialValues={initialValues}
        validationSchema={validationSchema}
        validate={validateContent}
        onSubmit={async (values) => {
          // ⚠️ Мутация перезаписывает ВСЕ поля задания, поэтому шлём их
          // целиком: отправив один только textbookRef, форма затёрла бы текст.
          await onSave({
            title: values.title.trim(),
            text: values.text.trim(),
            textbookRef: values.textbookRef.trim(),
            dueDate: values.dueDate ? DateTime.fromJSDate(values.dueDate).toISODate() : null,
          });
        }}>
        {({ values, errors, touched, setFieldValue, setFieldTouched, handleSubmit }) => (
          <Form onSubmit={handleSubmit}>
            <FieldLabel>{t('homework.assignment.form.titleLabel')}</FieldLabel>
            <Input
              value={values.title}
              onChange={(event) => setFieldValue('title', event.target.value)}
              onBlur={() => setFieldTouched('title')}
            />
            {touched.title && errors.title && <ErrorText>{errors.title}</ErrorText>}

            <FieldLabel>{t('homework.assignment.form.textLabel')}</FieldLabel>
            <Input.TextArea
              rows={6}
              value={values.text}
              onChange={(event) => setFieldValue('text', event.target.value)}
              onBlur={() => setFieldTouched('text')}
            />
            {touched.text && errors.text && <ErrorText>{errors.text}</ErrorText>}

            <FieldLabel>{t('homework.assignment.form.textbookRefLabel')}</FieldLabel>
            <Input
              value={values.textbookRef}
              maxLength={255}
              placeholder={`${t('homework.assignment.form.textbookRefPlaceholder')}`}
              onChange={(event) => setFieldValue('textbookRef', event.target.value)}
              onBlur={() => setFieldTouched('textbookRef')}
            />
            <FieldHint>{t('homework.assignment.form.textbookRefHint')}</FieldHint>

            <FieldLabel>{t('homework.assignment.form.dueDateLabel')}</FieldLabel>
            <DatePicker
              value={values.dueDate}
              onChange={(date) => setFieldValue('dueDate', date)}
            />

            <ButtonsWrapper>
              <Button type="button" variant="text" onClick={onCancel} disabled={saveLoading}>
                {t('homework.assignment.form.cancel')}
              </Button>
              <Button type="submit" isLoading={saveLoading} disabled={saveLoading}>
                {t('homework.assignment.form.save')}
              </Button>
            </ButtonsWrapper>
          </Form>
        )}
      </Formik>
    );
  },
);

const Form = styled.form`
  margin-top: 16px;
`;

const FieldLabel = styled.p`
  margin: 16px 0 4px;
  ${fontService.text({ type: TextType.normal, weight: FontWeight.medium })}

  &:first-child {
    margin-top: 0;
  }
`;

const FieldHint = styled.p`
  margin: 4px 0 0;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
`;

const ErrorText = styled.p`
  margin: 4px 0 0;
  color: ${Colors.signal_red};
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
`;

const ButtonsWrapper = styled.div`
  margin-top: 24px;
  display: flex;
  flex-direction: column-reverse;
  gap: 8px;

  ${tablet} {
    flex-direction: row;
    gap: 16px;
  }
`;
